// Start/end editor. Moving the start keeps the duration; all-day end dates are shown inclusive.
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Field, TimezoneSelect } from '@/components/app/common';
import { addDays, todayIn, wallMs, wallStr } from '@/lib/format';

export interface TimeValue {
  all_day: boolean;
  start: string;
  /** Inclusive last day for all-day events; wall-clock end for timed. */
  end: string;
  timezone: string;
}

/** Initial value from an event (all-day end is exclusive in storage). */
export function timeFromEvent(ev: { all_day: boolean; start: string; end?: string; timezone?: string }, feedTz: string): TimeValue {
  if (ev.all_day) return { all_day: true, start: ev.start.slice(0, 10), end: ev.end ? addDays(ev.end.slice(0, 10), -1) : ev.start.slice(0, 10), timezone: ev.timezone || feedTz };
  const s = ev.start.length > 10 ? ev.start.slice(0, 16) : `${ev.start}T09:00`;
  const e = ev.end && ev.end.length > 10 ? ev.end.slice(0, 16) : wallStr(wallMs(s) + 3600000);
  return { all_day: false, start: s, end: e, timezone: ev.timezone || feedTz };
}

export const timeValid = (v: TimeValue) => !!v.start && !(v.end && wallMs(v.end) < wallMs(v.start));

/** API payload: all-day ends are sent inclusive (end_inclusive). */
export function timePayload(v: TimeValue) {
  return v.all_day ? { all_day: true, start: v.start, end: v.end || v.start, end_inclusive: true } : { all_day: false, start: v.start, end: v.end || v.start, timezone: v.timezone };
}

export function TimeEditor({ value, onChange, feedTz, idPrefix = 't' }: { value: TimeValue; onChange: (v: TimeValue) => void; feedTz: string; idPrefix?: string }) {
  const [tzOpen, setTzOpen] = useState(value.timezone !== feedTz && !value.all_day);
  const dur = Math.max(wallMs(value.end) - wallMs(value.start), 0) || (value.all_day ? 0 : 3600000);
  const bad = !!value.end && wallMs(value.end) < wallMs(value.start);
  const type = value.all_day ? 'date' : 'datetime-local';
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
      <div className="flex items-center gap-2">
        <Switch
          id={`${idPrefix}-allday`}
          checked={value.all_day}
          onCheckedChange={(ad) => {
            const day = (value.start || todayIn(feedTz)).slice(0, 10);
            onChange(ad ? { ...value, all_day: true, start: day, end: value.end ? value.end.slice(0, 10) : day } : { ...value, all_day: false, start: `${day}T09:00`, end: `${day}T10:00` });
          }}
        />
        <Label htmlFor={`${idPrefix}-allday`}>All day</Label>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
        <Field label="Starts" htmlFor={`${idPrefix}-start`}>
          <Input
            id={`${idPrefix}-start`}
            name="start"
            type={type}
            value={value.start}
            onChange={(e) => {
              const s = e.target.value;
              onChange({ ...value, start: s, end: s ? wallStr(wallMs(s) + dur, value.all_day) : value.end });
            }}
          />
        </Field>
        <Field label="Ends" htmlFor={`${idPrefix}-end`}>
          <Input id={`${idPrefix}-end`} name="end" type={type} aria-invalid={bad} value={value.end} onChange={(e) => onChange({ ...value, end: e.target.value })} />
          {bad ? <p className="text-destructive text-xs" role="alert">Ends before it starts.</p> : null}
        </Field>
      </div>
      {!value.all_day ? (
        tzOpen ? (
          <Field label="Time zone" htmlFor={`${idPrefix}-tz`}>
            <TimezoneSelect id={`${idPrefix}-tz`} value={value.timezone} onChange={(tz) => onChange({ ...value, timezone: tz })} />
          </Field>
        ) : (
          <p className="text-muted-foreground text-xs">
            Time zone: {value.timezone.replace(/_/g, ' ')} ·{' '}
            <button type="button" className="text-primary font-medium hover:underline" onClick={() => setTzOpen(true)}>
              Change
            </button>
          </p>
        )
      ) : null}
    </div>
  );
}
